import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import toast from 'react-hot-toast';
import api from '../utils/api';

const IDLE_WARNING_MS = 25 * 60 * 1000; // 25 minutes
const IDLE_LOGOUT_MS = 30 * 60 * 1000;  // 30 minutes

let idleTimer = null;
let warningTimer = null;
let activityListeners = [];

function startIdleTracking(logoutFn) {
  stopIdleTracking();

  const resetTimers = () => {
    if (warningTimer) clearTimeout(warningTimer);
    if (idleTimer) clearTimeout(idleTimer);

    warningTimer = setTimeout(() => {
      toast('Your session will expire soon due to inactivity', { icon: '\u26A0\uFE0F', duration: 60000 });
    }, IDLE_WARNING_MS);

    idleTimer = setTimeout(() => {
      toast.dismiss();
      logoutFn();
    }, IDLE_LOGOUT_MS);
  };

  const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
  events.forEach((event) => {
    const handler = () => resetTimers();
    window.addEventListener(event, handler, { passive: true });
    activityListeners.push({ event, handler });
  });

  resetTimers();
}

function stopIdleTracking() {
  if (warningTimer) clearTimeout(warningTimer);
  if (idleTimer) clearTimeout(idleTimer);
  warningTimer = null;
  idleTimer = null;
  activityListeners.forEach(({ event, handler }) => {
    window.removeEventListener(event, handler);
  });
  activityListeners = [];
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          // Ensure CSRF cookie is set before login POST
          await api.get('/auth/csrf').catch(() => {});
          const response = await api.post('/auth/login', { email, password });
          const { token, refreshToken, user } = response.data;

          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          set({
            user,
            token,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });

          startIdleTracking(() => get().logout());

          return true;
        } catch (error) {
          const errorMessage = error.response?.data?.error || 'Login failed';
          set({
            error: errorMessage,
            isLoading: false,
          });

          // Show toast notification for specific errors
          if (errorMessage?.includes('Invalid email or password')) {
            toast.error('User not found or password is incorrect');
          } else if (errorMessage?.includes('Account is locked')) {
            toast.error('Account locked due to multiple failed attempts');
          } else if (errorMessage?.includes('disabled')) {
            toast.error('Your account has been disabled');
          } else if (errorMessage?.includes('deactivated') || errorMessage?.includes('suspended')) {
            toast.error('Your organization has been deactivated or suspended');
          } else {
            toast.error(errorMessage);
          }

          return false;
        }
      },

      logout: async () => {
        stopIdleTracking();

        try {
          await api.post('/auth/logout');
        } catch (error) {
          console.error('Logout error:', error);
        }

        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        delete api.defaults.headers.common['Authorization'];

        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      checkAuth: async () => {
        const token = localStorage.getItem('token');
        if (!token) {
          set({ isAuthenticated: false });
          return false;
        }

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        try {
          const response = await api.get('/auth/me');
          set({
            user: response.data,
            token,
            refreshToken: localStorage.getItem('refreshToken'),
            isAuthenticated: true,
          });
          startIdleTracking(() => get().logout());
          return true;
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          delete api.defaults.headers.common['Authorization'];
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
          });
          return false;
        }
      },

      updateUser: (userData) => {
        set({ user: { ...get().user, ...userData } });
      },

      refreshUser: async () => {
        try {
          const response = await api.get('/auth/me');
          set({ user: response.data });
          return true;
        } catch (error) {
          console.error('Failed to refresh user:', error);
          return false;
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
